/**
 * The correction log — Constitution invariants 4 and 8; Spec, *Corrections and
 * Trace* ("A correction is append-only. It never rewrites source evidence.");
 * architecture spec §3.
 *
 * A correction is an appended record, never an edit: it names the field it
 * contests, the corrected value, what it affects, its reason and its time. It
 * is also Evidence — the user's own words, with a stable id — so Trace can
 * list it in the chain and lineage can reference it. This module owns the
 * constructor, the Evidence form, the mapping from each reason onto a pipeline
 * effect, and the JSONL form the CLI reads and writes.
 *
 * Applied today: the `suppress` and `unsuppress` effects (routing reads them
 * through `suppression/`). Transitions, observations and history confirmations
 * are mapped here and applied by recomputation (M4.3).
 */
import { correctionId, evidenceId, sourceId } from '@cordelia/core'
import type {
  ClaimId,
  Correction,
  CorrectionReason,
  Evidence,
  HistoryClassification,
  LedgerItemId,
  OpennessState,
  PersonId,
  Suppression,
  TransitionType,
} from '@cordelia/core'
import { USER_SUPPRESSION_REASONS, makeSuppression } from '../suppression/index.js'
import type { UserSuppressionReason } from '../suppression/index.js'

/** The eight plain reasons of the correction sheet (M4.1) and the system-originated ones. */
export const CORRECTION_REASONS = [
  'not_a_commitment',
  'request_not_accepted',
  'wrong_person',
  'org_owes_not_me',
  'already_done',
  'wrong_timing',
  'irrelevant_or_private',
  'draft_abandoned',
  'history_confirmation',
  'suppression',
  'unsuppress',
  'delegation',
  'user_observation',
] as const satisfies readonly CorrectionReason[]

const REASONS: ReadonlySet<string> = new Set(CORRECTION_REASONS)

export type CorrectionInput = {
  readonly contested_field: string
  readonly corrected_value: unknown
  /**
   * What the contested field said before, read off the item at the moment the
   * correction is appended. Optional: a suppression contests no value, and the
   * first observation about an item replaces nothing.
   */
  readonly previous_value?: unknown
  readonly affects: readonly string[]
  readonly reason: CorrectionReason
  readonly time: string
}

/**
 * The only constructor. A correction without a contested field, a target, a
 * known reason or a time cannot exist. The id is a content hash, so the same
 * correction appended twice is one record.
 */
export function makeCorrection(input: CorrectionInput): Correction {
  const contested_field = input.contested_field.trim()
  if (contested_field.length === 0) throw new Error('a correction needs a contested field')
  const affects = input.affects.map((a) => a.trim()).filter((a) => a.length > 0)
  if (affects.length === 0) throw new Error('a correction needs at least one thing it affects')
  if (!REASONS.has(input.reason)) throw new Error(`unknown correction reason "${input.reason}"`)
  if (Number.isNaN(Date.parse(input.time))) throw new Error('a correction needs a time')
  return {
    id: correctionId(input.time, affects, contested_field),
    contested_field,
    corrected_value: input.corrected_value,
    previous_value: input.previous_value ?? null,
    affects: affects as unknown as Correction['affects'],
    time: input.time,
    reason: input.reason,
  }
}

/** A value as a person reads it, not as JSON quotes it. */
export function renderValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (value === null || value === undefined) return '(none)'
  return JSON.stringify(value)
}

/** One line a person can read in Trace, and the correction's `exact_quote`. */
export function renderCorrection(c: Correction): string {
  const line = `${c.reason}: ${c.contested_field} → ${renderValue(c.corrected_value)}`
  return c.previous_value === null || c.previous_value === undefined
    ? line
    : `${line} (previously ${renderValue(c.previous_value)})`
}

/**
 * What the contested field says on the item right now, so the correction being
 * appended can record what it replaced. Reading is all this does: the item is
 * never written.
 */
export function previousValueOf(
  item: Readonly<Record<string, unknown>> | null | undefined,
  contested_field: string,
): unknown {
  if (!item) return null
  return contested_field in item ? (item[contested_field] ?? null) : null
}

/** The correction's own source: it has no message, so its source is itself. */
export const correctionSourceId = (c: Correction) => sourceId('correction', c.id)

/**
 * A correction as Evidence: `source_type: 'correction'`, the user as speaker,
 * the correction's time, no span to match (the quote gate skips it), a stable
 * id from the correction's id.
 */
export function correctionEvidence(c: Correction, user: PersonId): Evidence {
  const source_id = correctionSourceId(c)
  return {
    id: evidenceId(source_id, 0, 0),
    source_id,
    source_type: 'correction',
    source_state: 'user',
    speaker: user,
    audience: [],
    exact_quote: renderCorrection(c),
    quote_span: null,
    timestamp: c.time,
  }
}

export type CorrectionEffect =
  /** Routing keeps the item in the ledger on the suppressed surface (applied today). */
  | { readonly kind: 'suppress'; readonly suppression: Suppression }
  /** Lifts the named suppression; the record stays (applied today). */
  | { readonly kind: 'unsuppress'; readonly suppression_id: string }
  /** Lineage records a transition on the affected claim (M4.3). */
  | { readonly kind: 'transition'; readonly type: TransitionType; readonly field: string }
  /** Openness reads a user observation (M4.3). */
  | { readonly kind: 'observation'; readonly openness: OpennessState | 'cancelled' }
  /** A Probably done? answer, counted apart from active-item actions (M4.5). */
  | { readonly kind: 'confirmation'; readonly classification: HistoryClassification }

const isItemId = (id: string): id is LedgerItemId => id.startsWith('it_')
const isClaimId = (id: string): id is ClaimId => id.startsWith('cl_')
const isUserReason = (r: unknown): r is UserSuppressionReason =>
  typeof r === 'string' && (USER_SUPPRESSION_REASONS as readonly string[]).includes(r)

function suppressionOf(c: Correction, reason: UserSuppressionReason): Suppression {
  const value = (c.corrected_value ?? {}) as { scope?: unknown; duration?: unknown }
  const item = c.affects.find((a) => isItemId(a))
  const claim = c.affects.find((a) => isClaimId(a))
  return makeSuppression({
    item_id: item && isItemId(item) ? item : null,
    claim_id: claim && isClaimId(claim) ? claim : null,
    reason,
    actor: 'user',
    time: c.time,
    ...(value.scope === 'lineage' || value.scope === 'counterparty' || value.scope === 'item'
      ? { scope: value.scope }
      : {}),
    ...(value.duration === 'forever' || value.duration === 'until_new_evidence'
      ? { duration: value.duration }
      : {}),
  })
}

/**
 * What each correction does to the pipeline's inputs. The table is the
 * contract between the correction sheet (M4.1), the log (M4.2) and
 * recomputation (M4.3); nothing here edits a source or a claim in place.
 */
export function correctionEffect(c: Correction): CorrectionEffect {
  switch (c.reason) {
    case 'suppression': {
      const value = c.corrected_value as { reason?: unknown } | string | null | undefined
      const reason = typeof value === 'string' ? value : value?.reason
      return {
        kind: 'suppress',
        suppression: suppressionOf(c, isUserReason(reason) ? reason : 'user:dismiss'),
      }
    }
    case 'irrelevant_or_private':
      return { kind: 'suppress', suppression: suppressionOf(c, 'user:irrelevant_or_private') }
    case 'draft_abandoned':
      return { kind: 'suppress', suppression: suppressionOf(c, 'user:draft_abandoned') }
    case 'unsuppress':
      return { kind: 'unsuppress', suppression_id: String(c.corrected_value) }
    case 'not_a_commitment':
      return { kind: 'transition', type: 'dispute', field: 'existence' }
    case 'request_not_accepted':
      return { kind: 'transition', type: 'modification', field: 'claim_type' }
    case 'wrong_person':
      return { kind: 'transition', type: 'modification', field: c.contested_field }
    case 'org_owes_not_me':
      return { kind: 'transition', type: 'modification', field: 'promise_scope' }
    case 'wrong_timing':
      return { kind: 'transition', type: 'modification', field: 'timing' }
    case 'delegation':
      return { kind: 'transition', type: 'delegation', field: 'responsible_actor' }
    case 'already_done':
      return { kind: 'observation', openness: 'resolved' }
    case 'user_observation': {
      const v = c.corrected_value
      return {
        kind: 'observation',
        openness: v === 'open' || v === 'cancelled' || v === 'resolved' ? v : 'resolved',
      }
    }
    case 'history_confirmation': {
      const v = c.corrected_value
      return {
        kind: 'confirmation',
        classification:
          v === 'done' || v === 'still_open' || v === 'delegated' || v === 'dropped' ? v : 'done',
      }
    }
  }
}

/** The log's suppression entries as the suppressions routing applies. One Dismiss is one appended correction. */
export function suppressionsFrom(corrections: readonly Correction[]): Suppression[] {
  const out: Suppression[] = []
  for (const c of corrections) {
    const effect = correctionEffect(c)
    if (effect.kind === 'suppress') out.push(effect.suppression)
  }
  return out
}

/** The CLI's log: one JSON object per line, in append order. */
export function serializeCorrectionLog(corrections: readonly Correction[]): string {
  return corrections.map((c) => JSON.stringify(c)).join('\n') + (corrections.length > 0 ? '\n' : '')
}

/** Reads a JSONL log; a malformed line names its number. Every entry is rebuilt through the constructor. */
export function parseCorrectionLog(text: string): Correction[] {
  const out: Correction[] = []
  const lines = text.split('\n')
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]?.trim() ?? ''
    if (line.length === 0) continue
    let raw: unknown
    try {
      raw = JSON.parse(line)
    } catch {
      throw new Error(`corrections log line ${i + 1}: not JSON`)
    }
    if (typeof raw !== 'object' || raw === null)
      throw new Error(`corrections log line ${i + 1}: not a correction`)
    const r = raw as Partial<CorrectionInput> & { readonly id?: unknown }
    let made: Correction
    try {
      made = makeCorrection({
        contested_field: String(r.contested_field ?? ''),
        corrected_value: r.corrected_value,
        previous_value: r.previous_value,
        affects: Array.isArray(r.affects) ? r.affects.map(String) : [],
        reason: r.reason as CorrectionReason,
        time: String(r.time ?? ''),
      })
    } catch (err) {
      throw new Error(`corrections log line ${i + 1}: ${(err as Error).message}`, { cause: err })
    }
    if (typeof r.id === 'string' && r.id !== made.id)
      throw new Error(`corrections log line ${i + 1}: id does not match its content`)
    out.push(made)
  }
  return out
}
